import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthProvider';
import { ChevronDown, User, Settings, HelpCircle, LogOut, Key } from 'lucide-react';
import ChangePasswordModal from './ChangePasswordModal';
import Alert from '../Alert/Alert';

const UserMenu = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const toggleRef = useRef(null);
  const [profileImage, setProfileImage] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [successToast, setSuccessToast] = useState(false);
  
  // Get user initials for fallback
  const getUserInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Load profile image
  useEffect(() => {
    if (user?.photo) {
      setImageError(false);
      setProfileImage(user.photo);
    } else {
      setProfileImage(null);
    }
  }, [user?.photo]);

  const handleImageError = () => {
    setImageError(true);
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  const handleProfileClick = () => {
    // Navigate to profile page
    navigate('/profile');
  };

  const handleSettingsClick = () => {
    // Navigate to settings page
    console.log('Navigate to settings');
  };

  const handleChangePasswordClick = () => {
    setIsChangePasswordModalOpen(true);
  };

  const handleChangePasswordSubmit = async () => {
    setIsChangePasswordModalOpen(false);
    setSuccessToast(true);
    setTimeout(() => setSuccessToast(false), 4000);
  };

  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  // React owns the menu state so desktop and mobile have the same reliable behavior.
  useEffect(() => {
    const onDocClick = (e) => {
      const el = containerRef.current;
      if (!el) return;
      if (!el.contains(e.target) && isOpen) {
        setIsOpen(false);
        if (process.env.NODE_ENV !== 'production') {
          console.log('[UserMenu] closing because click outside ->', e.target);
        }
      }
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', onDocClick, true);
    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('click', onDocClick, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className="nav-item dropdown">
      <button
        ref={toggleRef}
        type="button"
        className="nav-link d-flex align-items-center text-reset px-2 py-1"
        role="button"
        aria-label="Open user menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(prev => !prev)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        style={{
          borderRadius: '0.5rem',
          transition: 'all 0.2s ease-in-out',
          minHeight: '40px',
          cursor: 'pointer',
          backgroundColor: 'transparent',
          boxShadow: (isFocused || isOpen) ? '0 0 0 0.15rem rgba(13,110,253,0.15)' : 'none'
        }}
      >
        <span 
          className="avatar avatar-sm position-relative flex-shrink-0 d-flex align-items-center justify-content-center"
          style={{ 
            backgroundImage: profileImage && !imageError ? `url(${profileImage})` : 'none',
            backgroundColor: !profileImage || imageError ? '#6c757d' : 'transparent',
            border: '2px solid #e9ecef',
            transition: 'all 0.2s ease-in-out',
            color: 'white',
            fontWeight: '600',
            fontSize: '0.75rem'
          }}
        >
          {profileImage && !imageError && (
            <img 
              src={profileImage} 
              alt="Profile" 
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover', 
                borderRadius: '50%' 
              }}
              onError={handleImageError}
            />
          )}
          {(!profileImage || imageError) && (
            <span>{getUserInitials(user?.username || user?.name)}</span>
          )}
          <span 
            className="badge bg-success position-absolute" 
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              bottom: '2px',
              right: '2px',
              border: '2px solid white'
            }}
          ></span>
        </span>
        
        <div className="d-none d-lg-block ms-2 flex-grow-1" style={{ minWidth: 0, textAlign: 'left' }}>
          <div className="fw-semibold text-dark text-truncate" style={{ fontSize: '0.875rem', lineHeight: '1.2' }}>
            {user?.username || 'User'}
          </div>
          <div className="mt-1">
            <span 
              className="small text-secondary" 
              style={{ 
                fontSize: '0.7rem',
                fontWeight: '400',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block'
              }}
            >
              {user?.jobTitle || '(No Job Title)'}
            </span>
          </div>
        </div>
        
        <div className="d-none d-lg-block ms-2 flex-shrink-0">
          <ChevronDown 
            className="text-muted" 
            size={16}
          />
        </div>
      </button>
      
      <div className={`dropdown-menu dropdown-menu-end dropdown-menu-arrow user-account-menu${isOpen ? ' show' : ''}`}
           style={{ 
             minWidth: '200px',
             boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
             border: '1px solid rgba(0, 0, 0, 0.05)',
             borderRadius: '0.5rem'
           }}>
        
        {/* Menu Items */}
        <a 
          href="#" 
          className="dropdown-item d-flex align-items-center py-2 px-3" 
          onClick={(e) => {
            e.preventDefault();
            handleProfileClick();
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f8f9fa';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
          }}
          onMouseOut={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              e.target.style.backgroundColor = 'transparent';
            }
          }}
        >
          <User className="me-3" size={20} />
          <span>Profile</span>
        </a>

        {/* <a 
          href="#" 
          className="dropdown-item d-flex align-items-center py-2 px-3" 
          onClick={(e) => {
            e.preventDefault();
            handleSettingsClick();
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f8f9fa';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
          }}
          onMouseOut={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              e.target.style.backgroundColor = 'transparent';
            }
          }}
        >
          <Settings className="me-3" size={20} />
          <span>Settings</span>
        </a> */}

        <a 
          href="#" 
          className="dropdown-item d-flex align-items-center py-2 px-3" 
          onClick={(e) => {
            e.preventDefault();
            handleChangePasswordClick();
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f8f9fa';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
          }}
          onMouseOut={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              e.target.style.backgroundColor = 'transparent';
            }
          }}
        >
          <Key className="me-3" size={20} />
          <span>Change Password</span>
        </a>

        <a 
          href="https://support.cbqaglobal.co.id/" 
          className="dropdown-item d-flex align-items-center py-2 px-3" 
          target="_blank"
          rel="noopener noreferrer"
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f8f9fa';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
          }}
          onMouseOut={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              e.target.style.backgroundColor = 'transparent';
            }
          }}
        >
          <HelpCircle className="me-3" size={20} />
          <span>Help & Support</span>
        </a>

        <div className="dropdown-divider my-2"></div>

        <a 
          href="#" 
          className="dropdown-item d-flex align-items-center py-2 px-3 text-danger" 
          onClick={(e) => {
            e.preventDefault();
            handleLogout();
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#fef2f2';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
          }}
          onMouseOut={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              e.target.style.backgroundColor = 'transparent';
            }
          }}
        >
          <LogOut className="me-3" size={20} />
          <span>Logout</span>
        </a>
      </div>
      
      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
        onSubmit={handleChangePasswordSubmit}
      />

      {successToast && (
        <Alert
          type="success"
          message="Password changed successfully!"
          onClose={() => setSuccessToast(false)}
        />
      )}
    </div>
  );
};

export default UserMenu;
